import * as React from 'react'

import { CONTENT_WRAPPER_CLASS_NAME } from '../constants'
import { useVersionComparisonStore } from '../store'

const ZOOM_MIN = 0.5
const ZOOM_MAX = 3
const ZOOM_STEP = 0.25

/**
 * Applies zoom scale to a content-wrapper element inside a shadow root
 */
const applyZoomToContentWrapper = (
  contentWrapper: HTMLElement,
  zoomScale: number
) => {
  contentWrapper.style.transform = `scale(${zoomScale})`
  contentWrapper.style.transformOrigin = 'top left'
}

/**
 * Applies zoom to all existing layouts by finding shadow root wrappers
 * using version IDs from compareVersions
 * Only applies to versions that are ready (isReady === true)
 */
const applyZoomToAllLayouts = (
  compareVersions: Array<{ id: string; isReady?: boolean }> | null,
  zoomScale: number
) => {
  if (!compareVersions || compareVersions.length === 0) {
    return
  }

  compareVersions.forEach((version) => {
    // Only apply zoom to ready versions
    if (!version.isReady) return

    // Find the shadow root wrapper div by version ID
    const shadowHostElement = document.getElementById(version.id)
    if (!shadowHostElement) return

    // Get the shadow root from the host element
    const shadowRoot = shadowHostElement.shadowRoot
    if (!shadowRoot) return

    // Find the content-wrapper inside the shadow root
    const contentWrapper = shadowRoot.querySelector(
      `.${CONTENT_WRAPPER_CLASS_NAME}`
    ) as HTMLElement

    if (contentWrapper) applyZoomToContentWrapper(contentWrapper, zoomScale)
  })
}

/**
 * Hook to manage zoom control for document content
 *
 * Manages zoom state in the store and provides functions to zoom in/out.
 * The zoom scale is applied to all content-wrapper elements inside shadow roots
 * of all existing layouts.
 *
 * @param shadowRootRef - Optional ref to a specific shadow root (for backward compatibility)
 * @param isReady - Whether the layout is ready to use zoom features
 * @returns Object with zoom state and control functions
 */
export const useZoomControl = (
  shadowRootRef?: React.RefObject<ShadowRoot | null>,
  isReady: boolean = false
) => {
  const { zoomScale, setZoomScale, compareVersions } =
    useVersionComparisonStore()

  // Apply zoom to all existing layouts when zoom scale changes (only if ready)
  React.useEffect(() => {
    if (!isReady) return
    applyZoomToAllLayouts(compareVersions, zoomScale)
  }, [zoomScale, compareVersions, isReady])

  // Watch for new shadow root wrappers being added to the DOM and apply zoom
  React.useEffect(() => {
    if (!compareVersions || compareVersions.length === 0) return

    // Create a set of version IDs to watch for
    const versionIds = new Set(compareVersions.map((v) => v.id))

    // Use MutationObserver to watch for new shadow root wrappers
    const observer = new MutationObserver(() => {
      // Check all version IDs and apply zoom if shadow root is available
      versionIds.forEach((versionId) => {
        const shadowHostElement = document.getElementById(versionId)
        if (!shadowHostElement) return

        const shadowRoot = shadowHostElement.shadowRoot
        if (!shadowRoot) return

        const contentWrapper = shadowRoot.querySelector(
          `.${CONTENT_WRAPPER_CLASS_NAME}`
        ) as HTMLElement

        if (contentWrapper) applyZoomToContentWrapper(contentWrapper, zoomScale)
      })
    })

    // Observe the document body for new elements
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [zoomScale, compareVersions])

  // Also apply zoom to a specific shadow root if ref is provided (for backward compatibility)
  React.useEffect(() => {
    if (!shadowRootRef?.current) return

    const shadowRoot = shadowRootRef.current
    const contentWrapper = shadowRoot.querySelector(
      `.${CONTENT_WRAPPER_CLASS_NAME}`
    ) as HTMLElement

    if (contentWrapper) applyZoomToContentWrapper(contentWrapper, zoomScale)
  }, [zoomScale, shadowRootRef])

  // Use MutationObserver to detect when content-wrapper is added to a specific shadow root
  React.useEffect(() => {
    if (!shadowRootRef?.current) return

    const shadowRoot = shadowRootRef.current

    // Use MutationObserver to detect when content-wrapper is added
    const observer = new MutationObserver(() => {
      const contentWrapper = shadowRoot.querySelector(
        `.${CONTENT_WRAPPER_CLASS_NAME}`
      ) as HTMLElement

      if (contentWrapper) applyZoomToContentWrapper(contentWrapper, zoomScale)
    })

    observer.observe(shadowRoot, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [zoomScale, shadowRootRef])

  const zoomIn = React.useCallback(() => {
    if (!isReady) return
    const newScale = Math.min(zoomScale + ZOOM_STEP, ZOOM_MAX)
    setZoomScale(Math.round(newScale * 100) / 100) // Round to 2 decimal places
  }, [zoomScale, setZoomScale, isReady])

  const zoomOut = React.useCallback(() => {
    if (!isReady) {
      return
    }
    const newScale = Math.max(zoomScale - ZOOM_STEP, ZOOM_MIN)
    setZoomScale(Math.round(newScale * 100) / 100) // Round to 2 decimal places
  }, [zoomScale, setZoomScale, isReady])

  const resetZoom = React.useCallback(() => {
    setZoomScale(1) // ZOOM_DEFAULT
  }, [setZoomScale])

  const canZoomIn = React.useMemo(() => {
    return zoomScale < ZOOM_MAX
  }, [zoomScale])

  const canZoomOut = React.useMemo(() => {
    return zoomScale > ZOOM_MIN
  }, [zoomScale])

  return {
    zoomScale,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
  }
}
